import { useEffect, useRef } from 'react';
import clsx from 'clsx';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { outlinedInputClasses } from '@mui/material';
import {
  ComboBox,
  comboBoxClasses,
  Divider,
  generateUtilityClasses,
  Grid,
  Select,
  useCascadeState,
} from '@kc-console/common-ui';

import type { SeparatedPropertyFilterProps } from './types';

const SEPARATED_PROPERTY_FILTER_COMBO_BOX_PAPER_MAX_HEIGHT = 480;
const SEPARATED_PROPERTY_FILTER_GNB_OFFSET = 88;
const SEPARATED_PROPERTY_FILTER_VIEWPORT_MARGIN = 16;

export default function SeparatedPropertyFilter(props: SeparatedPropertyFilterProps) {
  const {
    autoFocus = true,
    className,
    disabled = false,
    filterKey: filterKeyProp,
    filterKeyOptions: filterKeyOptions,
    filterKeyDefaultOption,
    filterValue: filterValueProp,
    filterValueOptions: filterValueOptions,
    isFilterValueOptionsLoading,
    onFilterKeyChange: onFilterKeyChangeProp,
    onFilterValueChange: onFilterValueChangeProp,
    onSubmit,
    placeholder = 'Enter filter value...',
    ...others
  } = props;

  const keyContainerRef = useRef<HTMLDivElement | null>(null);
  const keyInputRef = useRef<HTMLInputElement | null>(null);
  const valueInputRef = useRef<HTMLInputElement | null>(null);

  const [filterKey, onFilterKeyChange] = useCascadeState({
    initialValue: filterKeyDefaultOption ?? filterKeyOptions.at(0)?.value ?? null,
    value: filterKeyProp,
    onChange: value => value !== null && onFilterKeyChangeProp?.(value),
  });

  const [filterValue, onFilterValueChange] = useCascadeState({
    initialValue: '',
    value: filterValueProp,
    onChange: value => value !== null && onFilterValueChangeProp?.(value),
  });

  const handleSubmit = ({
    filterKey,
    filterValue,
  }: {
    filterKey: string;
    filterValue: unknown;
  }) => {
    if (filterKey) {
      onSubmit?.({ filterKey, filterValue });
    }
  };

  const handleFilterKeyChange = (nextFilterKey: string | null) => {
    if (nextFilterKey === filterKey || nextFilterKey === null) return;
    onFilterKeyChange(nextFilterKey);
    setTimeout(() => valueInputRef.current?.focus());
  };

  const updateValueTimer = useRef<number | null>(null);

  const handleFilterValueChange = (event: unknown, value: string, reason: string) => {
    if (reason === 'input' || reason === 'clear') {
      onFilterValueChange(value);
    } else if (filterKey && reason === 'selectOption' && value) {
      if (updateValueTimer.current) clearTimeout(updateValueTimer.current);
      handleSubmit({ filterKey: filterKey, filterValue: value });
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing && filterKey && filterValue) {
      if (updateValueTimer.current) clearTimeout(updateValueTimer.current);
      updateValueTimer.current = window.setTimeout(() => {
        if (!filterKey) return;
        handleSubmit({ filterKey: filterKey, filterValue: filterValue });
      });
    }
  };

  const isAllFilterKeyOptionsDisabled = filterKeyOptions.every(option => option.disabled);
  const isPropertyFilterUnavailable = disabled || isAllFilterKeyOptionsDisabled;

  useEffect(() => {
    if (autoFocus) {
      if (autoFocus === 'key') {
        keyInputRef.current?.focus();
      }
      if (autoFocus === true || autoFocus === 'value') {
        valueInputRef.current?.focus();
      }
    }
  }, []);

  useEffect(() => {
    const selectedFilterKeyOption = filterKeyOptions.find(option => option.value === filterKey);
    if (!selectedFilterKeyOption?.disabled) return;

    const fallbackFilterKeyOption =
      filterKeyOptions.find(option => !option.disabled) ?? filterKeyOptions.at(0);

    if (fallbackFilterKeyOption && fallbackFilterKeyOption.value !== filterKey) {
      onFilterKeyChange(fallbackFilterKeyOption.value);
    }
  }, [filterKey, filterKeyOptions, onFilterKeyChange]);

  return (
    <SeparatedPropertyFilterRoot
      className={clsx(separatedPropertyFilterClasses.root, className)}
      container
      wrap="nowrap"
      {...others}
    >
      <SeparatedPropertyFilterKeySelect
        className={separatedPropertyFilterClasses.keySelect}
        size="md"
        onChange={handleFilterKeyChange}
        options={filterKeyOptions}
        value={filterKey}
        disabled={isPropertyFilterUnavailable}
        autoWidth={200}
        ref={keyContainerRef}
        inputRef={keyInputRef}
      />
      <SeparatedPropertyFilterDivider
        className={separatedPropertyFilterClasses.divider}
        orientation="vertical"
      />
      <SeparatedPropertyFilterValueInput
        className={separatedPropertyFilterClasses.valueInput}
        options={filterValueOptions?.map(o => o.text ?? '') ?? []}
        value={filterValue}
        onChange={handleFilterValueChange}
        size="md"
        placeholder={placeholder}
        loading={isFilterValueOptionsLoading}
        disabled={isPropertyFilterUnavailable}
        onKeyDown={handleInputKeyDown}
        inputRef={valueInputRef}
        getOptionDisabled={option =>
          filterValueOptions?.some(o => o.text === option && o.disabled) ?? false
        }
        slotProps={{
          popper: {
            sx: {
              [`& .${comboBoxClasses.paper}`]: {
                maxHeight: `min(${SEPARATED_PROPERTY_FILTER_COMBO_BOX_PAPER_MAX_HEIGHT}px, calc(100vh - ${SEPARATED_PROPERTY_FILTER_GNB_OFFSET}px - ${SEPARATED_PROPERTY_FILTER_VIEWPORT_MARGIN * 2}px))`,
              },
            },
          },
        }}
      />
    </SeparatedPropertyFilterRoot>
  );
}

const separatedPropertyFilterClasses = generateUtilityClasses('SeparatedPropertyFilter', [
  'root',
  'keySelect',
  'valueInput',
  'divider',
]);

const SeparatedPropertyFilterRoot = styled(Grid)`
  position: relative;
  width: 440px;
`;

const SeparatedPropertyFilterKeySelect = styled(Select<string | null>)(
  () => css`
    min-width: 0;
    max-width: 140px;
    flex-shrink: 0;
    & .${outlinedInputClasses.notchedOutline} {
      border-right: none;
    }
  `,
);

const SeparatedPropertyFilterDivider = styled(Divider)(
  ({ theme }) => css`
    .${separatedPropertyFilterClasses.root} & {
      margin-left: -0.5px;
      margin-right: -0.5px;
      height: 20px;
      align-self: center;
      z-index: ${theme.zIndex.raised};
    }
  `,
);

const SeparatedPropertyFilterValueInput = styled(ComboBox)(
  () => css`
    min-width: 0;
    flex: 1 1 auto;
    & .${outlinedInputClasses.notchedOutline} {
      border-left: none;
    }
  `,
);
